import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {MatSelectChange} from '@angular/material/select';
import {Subscription} from 'rxjs';
import {Pageable} from '../../../../shared/models/datasource/pageable';
import {ResponseStatsObservable} from '../../../../shared/models/datasource/response-stats-observable';

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss']
})
export class PaginatorComponent implements OnInit, AfterViewInit {
  initialized = false;
  responseStatsSubscription: Subscription;
  size = 0;
  page = 0;
  totalSize = 0;
  currentItems = 0;
  p: Pageable;

  @Input() entryRange: number[] = [20, 50, 100];
  @Input() pageQueryParam = 'page';
  @Input() sizeQueryParam = 'size';
  @Input() displayedPages = 5;
  @Input() hideEntrySelection = false;

  constructor(private router: Router, private route: ActivatedRoute) {
    this.subscribeForQueryParamChanges();
  }

  @Input() set pageable(pageable: Pageable) {
    this.p = pageable;
    if (this.initialized) {
      this.reset();
    }
  }

  @Input() set responseStatsObservable(responseStatsObservable: ResponseStatsObservable) {
    if (this.responseStatsSubscription) {
      this.responseStatsSubscription.unsubscribe();
    }
    if (responseStatsObservable) {
      this.responseStatsSubscription = responseStatsObservable.responseStatsObservable().subscribe(response => {
        this.totalSize = response.totalSize;
        this.currentItems = response.itemsLength;
      });
    }
  }

  get totalPages() {
    return this.totalSize === 0 ? 0 : Math.ceil(this.totalSize / this.size);
  }

  get paginatorRange() {
    const items: number[] = [];
    let start;
    let end;
    if (this.totalPages <= this.displayedPages) {
      start = 1;
      end = this.totalPages;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(this.displayedPages / 2);
      const maxPagesAfterCurrentPage = Math.ceil(this.displayedPages / 2) - 1;
      if (this.page <= maxPagesBeforeCurrentPage) {
        start = 1;
        end = this.displayedPages;
      } else if (this.page + maxPagesAfterCurrentPage >= this.totalPages) {
        start = this.totalPages - this.displayedPages + 1;
        end = this.totalPages;
      } else {
        start = this.page - maxPagesBeforeCurrentPage;
        end = this.page + maxPagesAfterCurrentPage;
      }
    }
    for (let i = start; i <= end; i++) {
      items.push(i);
    }
    return items;
  }

  ngOnInit(): void {
    this.page = +this.route.snapshot.queryParams[this.pageQueryParam] || 1;
    this.size = this.validatePageSize(+this.route.snapshot.queryParams[this.sizeQueryParam]) || this.entryRange[0];
    this.setPagination(this.page, this.size);
  }

  ngAfterViewInit(): void {
    this.initialized = true;
  }

  onPageNumberClicked(page: number) {
    this.setPaginationQueryParams(page, this.size);
  }

  onFirstPageClicked() {
    this.setPaginationQueryParams(1, this.size);
  }

  onPreviousPageClicked() {
    this.setPaginationQueryParams(this.page - 1, this.size);
  }

  onNextPageClicked() {
    this.setPaginationQueryParams(this.page + 1, this.size);
  }

  onLastPageClicked() {
    this.setPaginationQueryParams(this.totalPages, this.size);
  }

  onPageSizeChanged(event: MatSelectChange) {
    this.setPaginationQueryParams(1, event.value);
  }

  refresh() {
    const page = +this.route.snapshot.queryParams[this.pageQueryParam] || 1;
    const size = this.validatePageSize(+this.route.snapshot.queryParams[this.sizeQueryParam]) || this.entryRange[0];
    this.setPagination(page, size);
  }

  reset() {
    if (this.page === 1) {
      this.setPagination(this.page, this.size);
    } else {
      this.setPaginationQueryParams(1, this.size);
    }
  }

  private setPaginationQueryParams(page, size) {
    const queryParams = {};
    queryParams[this.pageQueryParam] = page;
    queryParams[this.sizeQueryParam] = size;
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
      }).then(event => {
      if (event !== null && !event) {
        this.setPaginationQueryParams(page, size);
      }
    });
  }

  private subscribeForQueryParamChanges() {
    this.route.queryParams.subscribe(values => {
      const page = +values[this.pageQueryParam] || 1;
      const size = this.validatePageSize(+values[this.sizeQueryParam]) || this.entryRange[0];
      if (this.page !== +values[this.pageQueryParam] || this.size !== +values[this.sizeQueryParam]) {
        this.page = page;
        this.size = size;
        this.setPagination(page, size);
      }
    });
  }

  private setPagination(page, size) {
    if (this.p) {
      this.p.setPageAndSize(page - 1, size);
    }
  }

  private validatePageSize(size: number) {
    if (this.entryRange.includes(size)) {
      return size;
    } else {
      return undefined;
    }
  }
}
